/**
 * Bulk actions on the bookings list (#3016): which rows are selected, which
 * selected bookings an action applies to, and the run that applies it one
 * booking at a time and reports every outcome.
 *
 * Pure on purpose: no store, no API slice. The page passes the per-booking
 * call in, so the rules here are testable without Redux or Auth0, and the
 * runner cannot decide on its own what "done" means.
 */
import type { Booking } from '../pages/rentals/Bookings/types';
import { isAwaitingApproval } from './bookingApproval';
import { getApiErrorField, getApiErrorMessage, getApiErrorStatus } from './apiError';

// ── Selection ───────────────────────────────────────────────────────────

/** The selection with `id` flipped. Never mutates the set it is given. */
export const toggleSelected = (selected: ReadonlySet<string>, id: string): Set<string> => {
    const next = new Set(selected);
    if (next.has(id)) {
        next.delete(id);
    } else {
        next.add(id);
    }
    return next;
};

export type HeaderSelectionState = 'none' | 'some' | 'all';

/** What the select-all box shows for the rows it covers. */
export const headerSelectionState = (
    visibleIds: readonly string[],
    selected: ReadonlySet<string>,
): HeaderSelectionState => {
    if (visibleIds.length === 0) return 'none';
    const count = visibleIds.filter((id) => selected.has(id)).length;
    if (count === 0) return 'none';
    return count === visibleIds.length ? 'all' : 'some';
};

/**
 * The select-all box: with every covered row ticked it clears them, otherwise
 * it ticks them all. Rows outside `visibleIds` (another page) keep their state.
 */
export const toggleAllVisible = (
    visibleIds: readonly string[],
    selected: ReadonlySet<string>,
): Set<string> => {
    const next = new Set(selected);
    if (headerSelectionState(visibleIds, selected) === 'all') {
        visibleIds.forEach((id) => next.delete(id));
    } else {
        visibleIds.forEach((id) => next.add(id));
    }
    return next;
};

/**
 * The selected bookings among those the list currently shows, in list order.
 * An id that dropped out of the list (a refetch, a narrowed filter) is never
 * acted on, whatever the selection still holds.
 */
export const selectedBookingsOf = (bookings: readonly Booking[], selected: ReadonlySet<string>): Booking[] =>
    bookings.filter((booking) => selected.has(booking.bookingId));

// ── Eligibility ─────────────────────────────────────────────────────────

/** The i18n key of why a selected booking is left out, or null when it is in. */
export type SkipCheck = (booking: Booking) => string | null;

/** Approve: only a reservation still awaiting approval (M8, #1809). */
export const approveSkipReason: SkipCheck = (booking) =>
    isAwaitingApproval(booking.approvalState) ? null : 'bookings.bulk.reason.not_awaiting_approval';

/**
 * Cancel: a booking that has not started and is not over — `booked` or
 * `reserved`, the status half of the detail pages' `isOpenForChange`
 * (BookingDetailsForNonRoomRental / ForRoomRental / ForVenueReservation). A
 * blocker (`blocked`) is not a booking and is removed on its own page. The
 * server stays the gate: it refuses an already-cancelled booking with 409.
 */
export const cancelSkipReason: SkipCheck = (booking) => {
    const status = booking.bookingStatus?.toLowerCase();
    return status === 'booked' || status === 'reserved' ? null : 'bookings.bulk.reason.not_cancelable';
};

// ── The run ─────────────────────────────────────────────────────────────

/** An i18n key with its parameters, or the server's own text. */
export interface BulkMessage {
    key?: string;
    params?: Record<string, string | number>;
    text?: string;
}

export interface BulkOutcome {
    booking: Booking;
    /** Skipped: why it was left out. Failed: why it failed. Succeeded: a warning, if any. */
    message?: BulkMessage;
}

export interface BulkResult {
    succeeded: BulkOutcome[];
    failed: BulkOutcome[];
    skipped: BulkOutcome[];
}

/**
 * One booking's action. Resolves when the booking was changed, with a warning
 * when something secondary did not happen (the mail, an invoice correction).
 * Rejects when the booking was not changed.
 */
export type BulkStep = (booking: Booking) => Promise<BulkMessage | undefined>;

/** A step's own refusal, with the message the summary shows for it. */
export class BulkStepError extends Error {
    readonly bulkMessage: BulkMessage;

    constructor(bulkMessage: BulkMessage) {
        super(bulkMessage.key ?? bulkMessage.text ?? 'bulk step failed');
        this.name = 'BulkStepError';
        this.bulkMessage = bulkMessage;
    }
}

/** Why one booking's call failed, as specifically as the response allows. */
export const bulkFailureMessage = (error: unknown): BulkMessage => {
    if (error instanceof BulkStepError) return error.bulkMessage;
    const reason = getApiErrorField(error, 'reason');
    if (reason === 'not_awaiting_approval') return { key: 'bookings.bulk.reason.not_awaiting_approval' };
    if (reason === 'fee_changed' || reason === 'fee_choice_required') return { key: 'bookings.bulk.reason.fee_changed' };
    // The new backend answers `{ message }`, the Lambda `{ msg }`.
    const text = getApiErrorField(error, 'msg') ?? getApiErrorMessage(error);
    if (text) return { text };
    const status = getApiErrorStatus(error);
    return status === undefined
        ? { key: 'bookings.bulk.reason.no_response' }
        : { key: 'bookings.bulk.reason.http_status', params: { status } };
};

/**
 * Apply `step` to every selected booking `skip` lets through, ONE AT A TIME:
 * each call takes the booking's own locks and sends its own mail, and a burst
 * of parallel writes against the same tenant is what those locks exist to
 * serialise anyway. A failure never stops the run and never counts as done:
 * every booking ends up in exactly one of the three lists.
 */
export const runBulkAction = async (
    bookings: readonly Booking[],
    skip: SkipCheck,
    step: BulkStep,
    onProgress?: (done: number, total: number) => void,
): Promise<BulkResult> => {
    const result: BulkResult = { succeeded: [], failed: [], skipped: [] };
    const eligible: Booking[] = [];
    for (const booking of bookings) {
        const skipKey = skip(booking);
        if (skipKey) {
            result.skipped.push({ booking, message: { key: skipKey } });
        } else {
            eligible.push(booking);
        }
    }
    let done = 0;
    onProgress?.(done, eligible.length);
    for (const booking of eligible) {
        try {
            const warning = await step(booking);
            result.succeeded.push({ booking, ...(warning ? { message: warning } : {}) });
        } catch (error) {
            result.failed.push({ booking, message: bulkFailureMessage(error) });
        }
        done += 1;
        onProgress?.(done, eligible.length);
    }
    return result;
};
